import type { CalculatorContent, Faq } from '@/types/calculator'
import type { RentalYieldInputs } from './types'

export const content: CalculatorContent<RentalYieldInputs> = {
  intro:
    'Compare gross vs net rental yield and estimate cash flow on an investment property with this rental yield calculator. Enter the purchase price, bond details, rent and running costs to see whether the numbers work.',
  howItWorks: [
    'Gross annual rent is reduced by a vacancy allowance to give effective income. Levies, rates, maintenance and management fees are deducted to reach net operating income.',
    'The bond repayment is estimated with the standard amortising formula, and annual cash flow is the net operating income minus the year’s bond payments.',
    'Gross yield divides annual rent by the purchase price; net yield uses net operating income instead. Cash-on-cash return divides annual cash flow by your deposit — the actual cash you invested.',
    'The first-year interest figure is a simplified approximation. Capital appreciation, income tax on rental profits and bond initiation costs are excluded.',
  ],
  formulas: [
    {
      label: 'Gross rental yield',
      expression: 'annual rent ÷ purchase price',
    },
    {
      label: 'Net rental yield',
      expression: 'net operating income ÷ purchase price',
      notes: 'Net operating income = effective rent − levies, rates, maintenance and management.',
    },
    {
      label: 'Annual cash flow',
      expression: 'net operating income − annual bond payments',
    },
    {
      label: 'Cash-on-cash return',
      expression: 'annual cash flow ÷ deposit',
    },
  ],
  example: {
    title: 'A R1.2 million flat renting for R10 000 a month',
    description:
      'An investor buys a sectional-title unit with a 20% deposit and wants the gross vs net yield and monthly cash flow after the bond.',
    inputs: {
      purchasePrice: 1_200_000,
      deposit: 240_000,
      bondInterestRate: 0.115,
      bondTermYears: 20,
      monthlyRent: 10_000,
      vacancyRate: 0.05,
      leviesMonthly: 1_800,
      ratesMonthly: 1_200,
      maintenancePercent: 0.05,
      managementPercent: 0.08,
    },
  },
  definitions: [
    {
      term: 'Gross yield',
      description:
        'Annual rent divided by the purchase price — a quick screening figure that ignores costs.',
    },
    {
      term: 'Net yield',
      description:
        'Net operating income divided by the purchase price — the truer return after vacancy and running costs.',
    },
    {
      term: 'Net operating income (NOI)',
      description:
        'Effective rental income after vacancy, levies, rates, maintenance and management — before bond payments.',
    },
    {
      term: 'Cash-on-cash return',
      description:
        'Annual cash flow divided by the cash you invested (the deposit). Shows the return on your actual money, not the property price.',
    },
    {
      term: 'Vacancy rate',
      description:
        'The share of the year the property is expected to stand empty — a buffer against over-optimistic income.',
    },
  ],
}

export const faqs: Faq[] = [
  {
    question: 'What is a good rental yield in South Africa?',
    answer:
      'It depends on the area and property type — many investors look for a net yield that beats their bond rate. Use the calculator with real figures for the specific property.',
  },
  {
    question: 'What is the difference between gross and net yield?',
    answer:
      'Gross yield ignores costs; net yield deducts vacancy, levies, rates, maintenance and management. Net yield is the more honest number.',
  },
  {
    question: 'Does the calculator include tax on rental income?',
    answer:
      'No. Rental profit is taxable income in the owner’s hands — consult a tax practitioner for your position.',
  },
  {
    question: 'Why is cash flow negative on many properties?',
    answer:
      'High bond repayments relative to rent produce negative monthly cash flow early on. Investors sometimes accept this, betting on capital growth.',
  },
]
